<template>
  <div v-if="shouldRender" class="product-field-approval-results">
    <ProductApprovalResultField
      v-for="result in displayResults"
      :key="`product_ar_${result.approvalRequestResultId || result.usageType}_${result.usageId ?? ''}`"
      :from-admin="isApprovalEditable"
      :draft="host.getProductApprovalResultDraft(result)"
      :saved-reason="result.rejectReason"
      :result-code="result.resultCode"
      :result-key="host.getProductApprovalResultDraftKey(result)"
      :can-save="false"
      :saving="false"
      :force-show="isApprovalEditable"
      :show-reject-reason="shouldShowRejectReason"
      :show-save="false"
      :templates="fieldRejectTemplates"
      :actor="fieldRejectActor"
      :error="host.getFieldRejectErrorMessage ? host.getFieldRejectErrorMessage(result) : ''"
      @update:draft="host.setProductApprovalResultDraft(result, $event)"
      @update:resultCode="host.setProductApprovalResultCode(result, $event)"
      @clear-error="host.clearFieldRejectError && host.clearFieldRejectError(result)"
    />
  </div>
</template>

<script>
import ProductApprovalResultField from './ProductApprovalResultField.vue';
import {
  isProductApprovalRejectedRow,
  resolveProductRejectReasonText,
} from './productApprovalFieldMeta';
import { SHOW_EDIT_SUPPLIER_REJECT_REASON } from '@/constants/approvalRejectUi.js';

export default {
  name: 'ProductFieldApprovalResults',
  components: { ProductApprovalResultField },
  inject: {
    productApprovalHost: { default: null },
  },
  props: {
    /** 字段键，对应 Approval_Request_Content.UsageType（TEXT=DbColumnName，媒体=UsageType） */
    usageType: { type: [String, Array], required: true },
    /**
     * 列表字段 UsageId（如认证行 RID）。传入后只展示该行的驳回结果；
     * 主表字段不传。
     */
    usageId: { default: null },
  },
  computed: {
    host() {
      return this.productApprovalHost;
    },
    /** 仅审批模式可编辑驳回框；编辑模式 / 供应商只读展示原因 */
    isApprovalEditable() {
      return !!(this.host?.fromAdmin && this.host?.isApprovalMode);
    },
    usageTypes() {
      const raw = this.usageType;
      if (Array.isArray(raw)) {
        return raw.map((x) => String(x || '').trim()).filter(Boolean);
      }
      return String(raw || '')
        .split(',')
        .map((x) => x.trim())
        .filter(Boolean);
    },
    hasUsageIdFilter() {
      return this.usageId != null && String(this.usageId).trim() !== '';
    },
    /** 父级传了 :usage-id（列表行）；未传则按主表字段处理 */
    isListRowBound() {
      return Object.prototype.hasOwnProperty.call(this.$options.propsData || {}, 'usageId');
    },
    results() {
      if (!this.host || !this.usageTypes.length) return [];
      const rows = this.host.getProductApprovalResultsByUsageType(this.usageTypes);
      // 列表行：必须按 RID 过滤；新增行尚无 rid 时不展示，避免复制上一行驳回
      if (this.isListRowBound) {
        if (!this.hasUsageIdFilter) return [];
        const key = String(this.usageId);
        return rows.filter((row) => String(row?.usageId ?? '') === key);
      }
      return rows;
    },
    /** 编辑态：只展示 radio 已选且有驳回原因的子项 */
    resultsSelected() {
      return this.results.filter((row) => {
        if (!isProductApprovalRejectedRow(row)) return false;
        const draft = this.host?.getProductApprovalResultDraft
          ? this.host.getProductApprovalResultDraft(row)
          : '';
        const reason = resolveProductRejectReasonText(draft || row.rejectReason || '');
        return !!String(reason || '').trim();
      });
    },
    /** 审批模式列出全部行；编辑/供应商只列已勾选 radio 的行 */
    displayResults() {
      return this.isApprovalEditable ? this.results : this.resultsSelected;
    },
    shouldShowRejectReason() {
      if (this.isApprovalEditable) return this.host?.showProductRejectPanels === true;
      return this.host?.isProductRejectedStatus === true;
    },
    shouldRender() {
      if (!this.results.length) return false;
      if (this.host?.showProductRejectPanels === false) return false;

      // 审批模式：有审核结果行就显示可编辑驳回框
      if (this.isApprovalEditable) return true;

      // TEMP：编辑模式 / 供应商暂不展示驳回原因
      if (!SHOW_EDIT_SUPPLIER_REJECT_REASON) return false;

      // 编辑模式 / 供应商：仅 radio 已勾选的子项只读展示
      if (!this.shouldShowRejectReason) return false;
      return this.resultsSelected.length > 0;
    },
    fieldRejectTemplates() {
      if (!this.isApprovalEditable) return [];
      return this.host?.overallRejectTemplates || [];
    },
    fieldRejectActor() {
      return this.host?.approvalRejectBy || 'AI';
    },
  },
};
</script>

<style lang="scss">
/*
 * 间距目标：
 * - 控件 → 驳回框：4px
 * - 驳回框 → 下一控件：16px
 * theme-form 下 form-group 默认 20px !important，用 :has 改成 4px（不用负 margin，避免盖上传区）
 */
.product-field-approval-results {
  position: relative;
  clear: both;
  margin-top: 4px;
  margin-bottom: 16px;
  width: 100%;
  box-sizing: border-box;
}

.theme-form .form-group:has(+ .product-field-approval-results),
.form-group:has(+ .product-field-approval-results) {
  margin-bottom: 4px !important;
}

.theme-form .form-group:has(+ .product-field-approval-results) + .product-field-approval-results,
.form-group:has(+ .product-field-approval-results) + .product-field-approval-results {
  margin-top: 0;
}
</style>
